import * as React from 'react';
import {
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Alert,
} from 'reactstrap';

import styled from 'styled-components';
import { showKeyModal } from '../../operations/showKeyModal';
import { OktaAuth } from '@okta/okta-auth-js';
import * as Model from '../../models';
import { useDispatch } from 'react-redux';
import { InfoIcon } from '@primer/octicons-react';
import { useTheme } from '../../styles/useTheme';
import { TextField } from '../TextField/TextField';
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';

interface Component {
  inputValue?: string;
  name?: string;
  number: string;
}

export interface DispatchProps {
  getKeys: (
    auth: OktaAuth,
    account: Model.Account,
    role: Model.Role,
    duration: number
  ) => void;
}

const filter = createFilterOptions<Component>();

const ChangeModal = ({ closeModal, account, role, duration, auth, url }) => {
  const [changeNumber, setChangeNumber] = React.useState('');
  const [primaryCINumber, setPrimaryCINumber] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly Component[]>([]);
  const [loading, setLoading] = React.useState(false);

  const [theme] = useTheme();

  const dispatch = useDispatch();

  const handleOpen = () => {
    setOpen(true);
    (async () => {
      setLoading(true);
      try {
        const response = await fetch('/data/components.json');
        if (response.ok) {
          const buffer = await response.arrayBuffer();
          const text = new TextDecoder().decode(buffer);
          const data = JSON.parse(text);
          setOptions(data);
        }
      } catch (error) {
        console.error('Error fetching components:', error);
      }
      setLoading(false);
    })();
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  const handleSubmit = React.useCallback(async () => {
    // if a url is provided to the change modal, then we know we are coming from the "Console" button
    if (url) {
      if (changeNumber) {
        window.open(`${url}&changeNumber=${changeNumber}`);
      }
      if (primaryCINumber) {
        window.open(`${url}&primaryCINumber=${primaryCINumber}&description="${description}"`);
      }
      closeModal();
    } else {
      // if no url is provided, then we are coming from the "Get Keys" button/dropdown, so we need to show the Key Modal
      dispatch(
        showKeyModal(
          auth,
          account,
          role,
          duration,
          changeNumber,
          primaryCINumber,
          description
        )
      );
    }
  }, [url, changeNumber, primaryCINumber, closeModal, description, dispatch, auth, account, role, duration]);

  const handleReadOnlyKeys = React.useCallback(() => {
    // if a url is provided to the change modal, then we know we are coming from the "Console" button
    if (url) {
      const urlObj = url.replace(/([?&]role=)[^&]*/, '$1ReadOnly');
      window.open(urlObj.toString());
      closeModal();
    } else {
      dispatch(
        showKeyModal(
        auth,
        account,
        { ...role, name: 'ReadOnly' },
        duration,
        changeNumber,
        primaryCINumber,
        description
        )
      );
    }
  }, [url, closeModal, dispatch, auth, account, role, duration, changeNumber, primaryCINumber, description]);

  const isSubmitDisabled: boolean =
    (!changeNumber && !primaryCINumber) ||
    (!!changeNumber && !!primaryCINumber);

  return (
    <div>
      <StyledModalHeader toggle={closeModal}>
        AWS Account Access
      </StyledModalHeader>
      <StyledModalBody>
        <div style={{ marginBottom: '1rem' }}>
          <Label for="requestedDuration" style={{ fontWeight: 'bold' }}>
            Requested Key Duration:
          </Label>
          <div style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
            {duration} hour(s)
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <Label for="requestedRole" style={{ fontWeight: 'bold' }}>
            Requested Role:
          </Label>
          <div style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
            {role.name}
          </div>
        </div>
        <StyledAlert color="warning">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon size={24} />
            <div style={{ marginLeft: '1rem' }}>
              For the selected role, please provide either an existing
              ServiceNow Change Ticket # or a Component ID for ALKS to
              create a change ticket on your behalf.
            </div>
          </div>
        </StyledAlert>
        <Label style={{ fontWeight: 'bold' }} for="changeNumber">
          Existing Change Request
        </Label>
        <TextField
          id="changeNumber"
          label="CHGXXXXXXX"
          variant="outlined"
          value={changeNumber}
          sx={{ backgroundColor: theme.backgroundColor, color: theme.textColor }}
          onChange={(e) => setChangeNumber(e.target.value)}
          disabled={!!primaryCINumber}
          fullWidth
          multiline={false}
        />
        <div style={{ textAlign: 'center', margin: '1rem 0' }}>
          <span>OR</span>
        </div>
        <Label style={{ fontWeight: 'bold' }} for="workloadComponentId">
          Component ID
        </Label>
        <Autocomplete
          id="workloadComponentId"
          open={open}
          disabled={!!changeNumber}
          freeSolo
          slotProps={{
            paper: {
              sx: {
          backgroundColor: theme.backgroundColor,
          color: theme.textColor,
          border: `1px solid ${theme.borderColor}`,
              },
            },
          }}
          options={options}
          // getOptionLabel={(option) =>
          //   typeof option === 'object' && 'number' in option
          //     ? `${option.name} - [ ${option.number} ]`
          //     : option
          // }
          getOptionLabel={(option) => {
            // Value selected with enter, right from the input
            if (typeof option === 'string') {
              return option;
            }
            // Add "xxx" option created dynamically
            if (option.inputValue) {
              return `${option.number}`;
            }
            // Regular option
            return `[ ${option.number} ] - ${option.name}`;
          }}
          isOptionEqualToValue={(option, value) => option.number === value.number}
          loading={loading}
          onOpen={handleOpen}
          onClose={handleClose}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type to Search...Component ID or Name"
              variant="outlined"
              value=""
              multiline={false}
              InputProps={{
                ...params.InputProps,
                type: 'search',
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;
            // Suggest the creation of a new value
            const isExisting = options.some((option) => inputValue === option.number);
            if (inputValue !== '' && !isExisting) {
              filtered.push({
                number: inputValue,
                name: `Add "${inputValue}"`,
              });
            }
    
            return filtered;
          }}
          // onChange={(event, value) => {
          //   if (value) {
          //     const number = typeof value === 'object' && 'number' in value ? value.number : '';
          //     console.log('Selected number:', number);
          //     setPrimaryCINumber(number);
          //   } else {
          //     setPrimaryCINumber("");
          //   }
          // }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setPrimaryCINumber(newValue);
            } else {
              setPrimaryCINumber(newValue?.number || '');
            }
          }}
        />
        <div style={{ marginBottom: '1rem' }}></div>
        <Label style={{ fontWeight: 'bold' }} for="shortDescription">
          Description
        </Label>
        <TextField
          id="shortDescription"
          label="Details of the change"
          variant="outlined"
          value={description}
          multiline
          rows={2}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!!changeNumber}
          fullWidth
        />
      </StyledModalBody>
      <StyledModalFooter style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          color="secondary"
          onClick={handleReadOnlyKeys}
        >
          {url ? 'Read Only Console' : 'Read Only Keys'}
        </Button>
        <div>
          <Button
        color="primary"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        style={{ marginRight: '0.5rem' }}
          >
        Submit
          </Button>
          <Button color="secondary" onClick={closeModal}>
        Cancel
          </Button>
        </div>
      </StyledModalFooter>
    </div>
  );
};

const StyledModalBody = styled(ModalBody)`
  background-color: ${({ theme }) => theme.modal.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  border-color: ${({ theme }) => theme.borderColor};
`;

const StyledModalFooter = styled(ModalFooter)`
  background-color: ${({ theme }) => theme.modal.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  border-color: ${({ theme }) => theme.borderColor};
`;

const StyledAlert = styled(Alert)`
  color: ${({ theme }) => (theme.type === 'dark' ? 'secondary' : 'primary')};
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

export const StyledModalHeader = styled(ModalHeader)`
  background-color: ${(props) => props.theme.modal.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  border-color: ${({ theme }) => theme.borderColor};

  && .close {
    color: ${({ theme }) => theme.textColor};
    text-shadow: 0 1px 0 ${({ theme }) => theme.modal.backgroundColor};
  }
`;

export default ChangeModal;
